import { Box } from "@mui/material";
import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
} from "react";
import { useTranslation } from "react-i18next";
import {
  SITE_COLOR,
  SITE_EASE,
  SITE_VISUALLY_HIDDEN,
} from "src/domains/shared/constants/styles/site";
import { jumpToManualSection } from "./manualJump";
import { searchManual, type ManualSearchEntry } from "./manualSearchIndex";

const SEARCH_STYLES = {
  root: {
    position: "relative",
    maxWidth: "460px",
    marginTop: "28px",
    "& > svg": {
      position: "absolute",
      left: "16px",
      top: "16px",
      color: SITE_COLOR.ink3,
      pointerEvents: "none",
    },
  },
  input: {
    display: "block",
    width: "100%",
    height: "52px",
    border: `1px solid ${SITE_COLOR.line}`,
    borderRadius: "12px",
    padding: "0 16px 0 46px",
    font: "inherit",
    fontSize: "16px",
    color: SITE_COLOR.ink,
    background: SITE_COLOR.panel,
    transition: "border-color .2s,background-color .2s,box-shadow .2s",
    "&:focus": {
      outline: "none",
      background: "#fff",
      borderColor: SITE_COLOR.blue,
      boxShadow: "0 0 0 4px rgba(29,116,255,.14)",
    },
  },
  results: {
    position: "absolute",
    left: 0,
    right: 0,
    top: "calc(100% + 8px)",
    zIndex: 50,
    background: "#fff",
    borderRadius: "12px",
    boxShadow: "0 12px 40px rgba(28,44,66,.18)",
    padding: "6px",
    maxHeight: "420px",
    overflow: "auto",
    transformOrigin: "top",
    animation: `sitePop .18s ${SITE_EASE.out}`,
    "& a": {
      display: "block",
      padding: "10px 12px",
      borderRadius: "8px",
      fontSize: "15px",
    },
    "& a small": { display: "block", fontSize: "12px", color: SITE_COLOR.ink3 },
    "& a:hover, & a[aria-selected='true']": { background: SITE_COLOR.panel },
    "& p": { padding: "14px 12px", color: SITE_COLOR.ink3, fontSize: "14px" },
  },
} as const;

/** 매뉴얼 첫 화면 검색 칸(service-manual 「매뉴얼 검색」) — 절 제목·단계 문구에서 찾고, 고르면 그 절로 간다 */
export function ManualSearch({
  entries,
}: {
  entries: readonly ManualSearchEntry[];
}) {
  const { t } = useTranslation("landing");
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(0);
  const hits = searchManual(entries, query);
  const expanded = open && query.trim() !== "";
  const optionId = (index: number) => `${listId}-${index}`;

  useEffect(() => {
    if (!expanded) return undefined;
    const closeOutside = (event: globalThis.MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("click", closeOutside);
    return () => document.removeEventListener("click", closeOutside);
  }, [expanded]);

  const go = (id: string) => {
    setOpen(false);
    jumpToManualSection(id, { flash: true });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!expanded) return;
    const arrowKey = event.key === "ArrowDown" || event.key === "ArrowUp";
    if (arrowKey && hits.length) {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setSelected((index) => (index + step + hits.length) % hits.length);
    } else if (event.key === "Enter" && hits[selected]) {
      event.preventDefault();
      go(hits[selected].entry.id);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  };

  const onResultClick = (event: MouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault();
    go(id);
  };

  const announcement = (() => {
    if (!expanded) return "";
    return hits.length
      ? t("site.manual.searchCount", { count: hits.length })
      : t("site.manual.searchEmpty");
  })();

  return (
    <Box ref={rootRef} role="search" sx={SEARCH_STYLES.root}>
      <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
        <circle
          cx="9"
          cy="9"
          r="6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <path
          d="M13.5 13.5L18 18"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
      <Box
        component="input"
        type="search"
        role="combobox"
        autoComplete="off"
        placeholder={t("site.manual.searchPlaceholder")}
        aria-label={t("site.manual.searchLabel")}
        aria-controls={listId}
        aria-expanded={expanded && hits.length > 0}
        aria-autocomplete="list"
        aria-activedescendant={
          expanded && hits[selected] ? optionId(selected) : undefined
        }
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setSelected(0);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
        sx={SEARCH_STYLES.input}
      />
      <Box hidden={!expanded} sx={SEARCH_STYLES.results}>
        <Box
          role="listbox"
          id={listId}
          aria-label={t("site.manual.searchLabel")}
          hidden={!hits.length}
        >
          {hits.map(({ entry, snippet }, index) => (
            <a
              key={entry.id}
              id={optionId(index)}
              href={`#${entry.id}`}
              role="option"
              aria-selected={index === selected}
              onClick={(event) => onResultClick(event, entry.id)}
            >
              <small>{entry.chapter}</small>
              {entry.title}
              {snippet ? <small>…{snippet}…</small> : null}
            </a>
          ))}
        </Box>
        {expanded && !hits.length ? (
          <p>{t("site.manual.searchEmpty")}</p>
        ) : null}
      </Box>
      <Box component="p" aria-live="polite" sx={SITE_VISUALLY_HIDDEN}>
        {announcement}
      </Box>
    </Box>
  );
}
